import { Texture } from "pixi.js";

/**
 * The halo, baked once into a radial-gradient texture.
 *
 * ## Why not a blurred stack of circles
 *
 * The halo used to be eight concentric circles of rising alpha, drawn with
 * `blendMode: "add"` into a Container carrying a `BlurFilter` — the rings
 * approximating a gradient, the blur hiding the banding. It left a visible
 * rectangle around every pressed key.
 *
 * A filter is not a paint operation. Pixi measures the target's bounds,
 * takes a power-of-two texture from the pool, renders the children into it,
 * and draws that texture back as a quad. Two things follow, and both were
 * showing:
 *
 * 1. **The blur is clipped at the quad.** `BlurFilter` derives its padding
 *    from its strength, and a Gaussian tail is wider than that padding, so
 *    the glow stopped along a straight line instead of falling off. Against
 *    a near-black roll, an abrupt end to a soft glow reads as an edge.
 * 2. **The additive blending is isolated.** The children add against the
 *    transparent black of the render target, and the flattened result is
 *    composited with the container's own blend mode, which was normal. The
 *    glow was never actually added to the roll.
 *
 * A gradient needs neither. One texture, tinted per note, drawn additively:
 * no render target, no padding, no isolation, no extra passes for the
 * multi-pass blur — which the iPad was paying for on every frame a key was
 * held. And the falloff is a real gradient rather than eight steps smoothed
 * until they look like one.
 *
 * ## The two layers, and why the profiles look like that
 *
 * The stops below are the **cumulative** alpha of the stack they replace: a
 * point at distance `d` was covered by every circle of radius greater than
 * `d`, so the alpha it ended up with is the sum of theirs. Reading the
 * numbers off that sum keeps the glow the one it always was, minus the
 * banding. The coloured glow and the white core stay separate because a tint
 * multiplies: a white centre inside a tinted sprite would be tinted with it.
 */

/** Half the texture's side. Scaled down in use, so it can afford to be big. */
const TEXTURE_RADIUS = 128;

/** `[distance from centre in 0..1, alpha]`, centre first. */
type Stop = readonly [number, number];

/**
 * Cumulative alpha of the six coloured rings, normalised to 1 at the centre.
 * The sprite's own alpha carries the amplitude, see `GLOW_PEAK_ALPHA`.
 */
const GLOW_STOPS: readonly Stop[] = [
  [0.0, 1.0],
  [0.354, 0.64],
  [0.438, 0.384],
  [0.542, 0.208],
  [0.667, 0.096],
  [0.833, 0.032],
  [1.0, 0.0],
];

/**
 * Peak alpha of the glow sprite at full velocity.
 *
 * The rings it replaces summed to 1.25 there, and reusing that number made
 * the halo too bright the moment the compositing was fixed. Not a mistake in
 * the reading, a change of operation: those 1.25 were flattened and then
 * composited **normally**, which replaces the background in proportion to
 * alpha. Additively they are added to it, so everywhere the glow overlaps
 * something already bright — the note bar it sits on, the playhead line it
 * straddles — the two now sum instead of one hiding the other.
 *
 * The wide coloured glow is what covers those, so it takes the deeper cut.
 * With the core below, the centre reaches 1.25 at `ff` instead of 2.85: the
 * white-hot point stays white-hot, and everything outside it stops clipping.
 */
export const GLOW_PEAK_ALPHA = 0.45;

/** The white core: the two white rings, likewise normalised to 1. */
const CORE_STOPS: readonly Stop[] = [
  [0.0, 1.0],
  [0.615, 0.406],
  [1.0, 0.0],
];

/**
 * Peak alpha of the core sprite at full velocity, cut from the 1.6 the two
 * white rings summed to. Cut less than the glow: it covers a small disc and
 * being blown out is its job.
 */
export const CORE_PEAK_ALPHA = 0.8;

/**
 * The anamorphic streak: a long horizontal bar of light through the point of
 * impact, with the four diagonal arms drawn from the same texture rotated.
 *
 * It is what makes a bright spot read as **light** rather than as an object.
 * A round glow, however soft, is a ball sitting on the key; a streak is a
 * lens looking at something too bright for it. Camera artefact, but every
 * viewer has spent their life seeing bright lights through lenses, so it is
 * the shorthand the eye already knows.
 *
 * Two alpha profiles multiplied: a long, slow falloff along the bar and a
 * tight one across it. The canvas cannot multiply two gradients directly, so
 * the horizontal one is painted and the vertical one is then applied with
 * `destination-in`, which multiplies the destination's alpha by the source's.
 */
const STREAK_WIDTH = 512;
const STREAK_HEIGHT = 64;

/** Along the bar, from one end to the other. */
const STREAK_ALONG: readonly Stop[] = [
  [0.0, 0.0],
  [0.25, 0.06],
  [0.4, 0.25],
  [0.47, 0.7],
  [0.5, 1.0],
  [0.53, 0.7],
  [0.6, 0.25],
  [0.75, 0.06],
  [1.0, 0.0],
];

/** Across it. Tight, or the streak is a smear. */
const STREAK_ACROSS: readonly Stop[] = [
  [0.0, 0.0],
  [0.35, 0.08],
  [0.45, 0.5],
  [0.5, 1.0],
  [0.55, 0.5],
  [0.65, 0.08],
  [1.0, 0.0],
];

let streak: Texture | null = null;

export function haloStreakTexture(): Texture {
  streak ??= bakeStreak();

  return streak;
}

/** Sprite scale that gives the streak a half-length of `reach` pixels. */
export function streakScale(reach: number): number {
  return (reach * 2) / STREAK_WIDTH;
}

function bakeStreak(): Texture {
  const canvas = document.createElement("canvas");

  canvas.width = STREAK_WIDTH;
  canvas.height = STREAK_HEIGHT;

  const ctx = canvas.getContext("2d");

  if (ctx === null) {
    return Texture.WHITE;
  }

  ctx.fillStyle = ramp(ctx.createLinearGradient(0, 0, STREAK_WIDTH, 0), STREAK_ALONG);
  ctx.fillRect(0, 0, STREAK_WIDTH, STREAK_HEIGHT);

  // Multiplies what is already there by this second profile.
  ctx.globalCompositeOperation = "destination-in";
  ctx.fillStyle = ramp(
    ctx.createLinearGradient(0, 0, 0, STREAK_HEIGHT),
    STREAK_ACROSS,
  );
  ctx.fillRect(0, 0, STREAK_WIDTH, STREAK_HEIGHT);

  return Texture.from(canvas);
}

function ramp(gradient: CanvasGradient, stops: readonly Stop[]): CanvasGradient {
  for (const [at, alpha] of stops) {
    gradient.addColorStop(at, `rgba(255, 255, 255, ${alpha})`);
  }

  return gradient;
}

let glow: Texture | null = null;
let core: Texture | null = null;

/** The coloured glow, white so a sprite tint can colour it. */
export function haloGlowTexture(): Texture {
  glow ??= bake(GLOW_STOPS);

  return glow;
}

/** The white-hot centre, never tinted. */
export function haloCoreTexture(): Texture {
  core ??= bake(CORE_STOPS);

  return core;
}

function bake(stops: readonly Stop[]): Texture {
  const size = TEXTURE_RADIUS * 2;
  const canvas = document.createElement("canvas");

  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");

  if (ctx === null) {
    return Texture.WHITE;
  }

  const gradient = ctx.createRadialGradient(
    TEXTURE_RADIUS,
    TEXTURE_RADIUS,
    0,
    TEXTURE_RADIUS,
    TEXTURE_RADIUS,
    TEXTURE_RADIUS,
  );

  ctx.fillStyle = ramp(gradient, stops);
  ctx.fillRect(0, 0, size, size);

  return Texture.from(canvas);
}

/** Sprite scale that puts the texture's edge at `radius` pixels. */
export function haloScale(radius: number): number {
  return radius / TEXTURE_RADIUS;
}
